import React, { useState, useEffect } from 'react';
import { heroSlides as defaultSlides } from '../data/products';
import { getHeroSlides } from '../services/settings';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroSlides, setHeroSlides] = useState(defaultSlides);

  useEffect(() => {
    (async () => {
      try {
        const slides = await getHeroSlides();
        if (slides && slides.length > 0) {
          // Map database fields to component fields
          const mappedSlides = slides.map(slide => ({
            id: slide.id,
            title: slide.title,
            subtitle: slide.subtitle,
            image: slide.image_url || slide.image, // Support both field names
          }));
          setHeroSlides(mappedSlides);
        }
      } catch (err) {
        console.error('Failed to load hero slides:', err);
        // If database fails, keep using default slides
      }
    })();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <section id="hero" className="relative h-[calc(100vh-3.5rem)] min-h-[500px] md:h-screen overflow-hidden">
      {/* Background Slides */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('Hero image failed to load:', slide.image);
                e.target.src = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1200&h=600&fit=crop&crop=center';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 flex items-center justify-center h-full px-4 py-16 md:py-0">
        <div className="text-center text-white max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 bengali-text animate-fade-in-up leading-tight">
            {heroSlides[currentSlide].title}
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 bengali-text animate-fade-in-up-delay px-4">
            {heroSlides[currentSlide].subtitle}
          </p>
        </div>
      </div>

      {/* Slide Indicators - Hidden on very small screens, shown on mobile+ */}
      <div className="absolute bottom-16 md:bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex space-x-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-500 transform hover:scale-150 ${
                index === currentSlide
                  ? 'bg-orange-500 scale-125 shadow-lg shadow-orange-500/50 animate-pulse'
                  : 'bg-white/50 hover:bg-white/75 hover:shadow-lg'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll Down Indicator - Hidden on mobile to avoid conflict with bottom nav */}
      <div className="hidden md:flex absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="flex flex-col items-center space-y-2">
          <svg className="w-6 h-6 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
          <span className="text-white text-xs bengali-text animate-fade-in-out">নিচে দেখুন</span>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
